'use client';

import * as React from 'react';

/**
 * La shell emette `data-surface="cer"`: è su quell'attributo che
 * `styles/tokens.css` applica il blu di marca (#00AEEF/#0088cc) e il font dei
 * numeri del mock CER. Le pagine fuori dalla shell (login) restano sui token base.
 */
const SURFACE = 'cer';

export interface AppShellFrameProps {
	/** Fascia di avviso opzionale sopra l'header. */
	banner?: React.ReactNode;
	/** Header dell'app (tipicamente {@link AppHeader}). */
	header: React.ReactNode;
	/** Sidebar di navigazione. */
	sidebar: React.ReactNode;
	/** Contenuto della pagina corrente, renderizzato nell'area scrollabile principale. */
	children: React.ReactNode;
	/** Modal e altri portali montati a livello di shell (sopra tutto il resto). */
	overlays?: React.ReactNode;
}

/**
 * Struttura globale (chrome) dell'app autenticata: banner, header, sidebar e area
 * principale scrollabile. Possiede l'intero scrolling della shell: le pagine
 * figlie non devono gestire il proprio scroll.
 *
 * Sotto l'header non c'è nessuna barra di contesto: come nel mock `SLIMS-CER_v11.html`,
 * il contenuto della pagina attacca direttamente all'header.
 */
const AppShellFrame = ({ banner, header, sidebar, children, overlays }: AppShellFrameProps) => {
	// L'attributo sta anche su <html> perché dialog, popover e tendine delle
	// select escono in portale su document.body: fuori dal sottoalbero della
	// shell leggerebbero l'accento globale, e un modale ciano sopra un'app blu
	// è esattamente il tipo di deriva che questo token doveva evitare.
	React.useEffect(() => {
		const root = document.documentElement;
		const previous = root.dataset.surface;
		root.dataset.surface = SURFACE;
		return () => {
			if (previous === undefined) delete root.dataset.surface;
			else root.dataset.surface = previous;
		};
	}, []);

	return (
		<div data-surface={SURFACE} className="flex h-screen flex-col overflow-hidden bg-bg">
			{banner}
			{header}
			<div className="relative z-[1] flex min-h-0 flex-1 overflow-hidden">
				{sidebar}
				<main className="min-h-0 flex-1 overflow-y-auto p-5 animate-fade-in">{children}</main>
			</div>
			{overlays}
		</div>
	);
};
AppShellFrame.displayName = 'AppShellFrame';

export { AppShellFrame };
